import React, { useState } from 'react';
import { AppointmentTableView } from '../components/AppointmentTableView';
import { UpdateAppointmentForm } from '../components/UpdateAppointmentForm';
import { useAppointments } from '../hooks/useAppointments';
import { ConfirmationModal } from '@/components/ui';
import { showSuccessMessage, showApiError } from '@/lib/toast';

import { AppointmentControlPanel } from '../components/AppointmentControlPanel';
import { useDebounce } from '@/hooks/useDebounce';

import { exportAppointmentData } from '@/utils/AppointmentScheduled';
import { getCumulativeStatuses } from '@/constants/globalConstants';

export const AppointmentScheduledScreen = ({ 
  filters: globalFilters = {}, 
  onLeadClick
}) => {
  const [localSearch, setLocalSearch] = useState('');
  const debouncedSearch = useDebounce(localSearch, 500);
  
  const [localFilters, setLocalFilters] = useState({
    status: getCumulativeStatuses('appointment_scheduled'),
    date: 'all',
    fromDate: '',
    toDate: ''
  });

  const combinedFilters = {
    ...globalFilters,
    ...localFilters,
    search: debouncedSearch
  };

  const { 
    appointments, 
    isLoading, 
    updateAppointment, 
    deleteAppointment,
    isDeleting 
  } = useAppointments(combinedFilters);

  const handleFilterChange = (key, value) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isUpdateFormOpen, setIsUpdateFormOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false });
  const [isExportingData, setIsExportingData] = useState(false);

  const handleExport = async () => {
    try {
      setIsExportingData(true);
      exportAppointmentData(appointments);
      showSuccessMessage("Appointments exported successfully");
    } catch (e) {
      showApiError(e);
    } finally {
      setIsExportingData(false);
    }
  };

  const handleEditClick = (appt) => {
    setSelectedAppointment(appt);
    setIsUpdateFormOpen(true);
  };

  const handleUpdateSubmit = async (id, data) => {
    try {
      await updateAppointment({ id, data });
      showSuccessMessage("Appointment updated successfully");
    } catch (e) {
      showApiError(e);
      throw e; // so form stops loading
    }
  };

  const handleDeleteClick = (appt) => {
    setSelectedAppointment(appt);
    setConfirmState({
      isOpen: true,
      title: "Cancel Appointment",
      message: `Are you sure you want to remove the appointment for ${appt.customerName || appt.firstName}? The lead will be moved back to the New stage and appointment details will be cleared.`,
      onConfirm: async () => {
        try {
          setConfirmState(prev => ({ ...prev, loading: true }));
          await deleteAppointment(appt._id);
          showSuccessMessage("Appointment removed successfully");
          setConfirmState(prev => ({ ...prev, isOpen: false }));
        } catch (e) {
          showApiError(e);
        } finally {
          setConfirmState(prev => ({ ...prev, loading: false }));
        }
      }
    });
  };

  return (
    <div className="block">
      {/* <AppointmentControlPanel
        search={localSearch}
        setSearch={setLocalSearch}
        filters={localFilters}
        setFilters={handleFilterChange}
        onExport={handleExport}
        isExporting={isExportingData}
      /> */}
      
      <AppointmentTableView
        appointments={appointments}
        isLoading={isLoading}
        onAppointmentClick={(appt) => {
          if (onLeadClick) onLeadClick(appt);
        }}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
      />

      {isUpdateFormOpen && (
        <UpdateAppointmentForm
          isOpen={isUpdateFormOpen}
          onClose={() => {
            setIsUpdateFormOpen(false);
            setSelectedAppointment(null);
          }}
          initialData={selectedAppointment}
          onSubmit={handleUpdateSubmit}
        />
      )}

      {confirmState.isOpen && (
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          onClose={() => setConfirmState({ isOpen: false })}
          title={confirmState.title}
          message={confirmState.message}
          onConfirm={confirmState.onConfirm}
          variant={confirmState.variant || "danger"}
          confirmText={confirmState.confirmText || "Confirm"}
          loading={confirmState.loading || isDeleting}
        />
      )}
    </div>
  );
};
